import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Landmark, Lock } from 'lucide-react';
import { formatMoney } from '@tcrd/shared';
import { Badge, Button, Card, cn, EmptyState, Notice, Spinner, TextArea, TextField } from '@tcrd/ui';
import { Page } from '../../components/Page';
import { useToast } from '../../components/toast';
import { api, errorMessage } from '../../lib/api';
import { fmtDateTime } from '../../lib/format';

export interface CommissionDto {
  id: string;
  weekStart: string;
  weekEnd: string;
  earnings: string;
  percent: string;
  amount: string;
  status: 'PENDING' | 'REPORTED' | 'PAID' | 'WAIVED';
  reference: string | null;
  note: string | null;
  /** Fecha límite de pago (aviso del lunes + días de plazo). */
  dueAt: string;
  driver?: string;
}

interface Mine {
  enabled: boolean;
  percent: string;
  pending: string;
  graceDays: number;
  blocking: boolean;
  /** Cuenta pausada por comisión vencida. */
  blocked: { amount: string; count: number } | null;
  bankAccounts: Array<{ bank: string; accountType: string; number: string; holder: string; holderId?: string }>;
  items: CommissionDto[];
}

const STATUS: Record<CommissionDto['status'], { label: string; tone: 'warning' | 'neutral' | 'success' }> = {
  PENDING: { label: 'Pendiente', tone: 'warning' },
  REPORTED: { label: 'En revisión', tone: 'neutral' },
  PAID: { label: 'Pagada', tone: 'success' },
  WAIVED: { label: 'Perdonada', tone: 'success' },
};

const week = (c: CommissionDto) => {
  const f = (d: string) => new Date(`${d}T12:00:00`).toLocaleDateString('es-DO', { day: 'numeric', month: 'short' });
  return `${f(c.weekStart)} – ${f(c.weekEnd)}`;
};

export const useMyCommissions = () => useQuery({ queryKey: ['commissions', 'me'], queryFn: () => api<Mine>('/commissions/me') });

/** Conductor: comisión semanal (cada lunes, % de lo cobrado por viajes la semana anterior, sin propinas). */
export function CommissionsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useMyCommissions();
  const [refs, setRefs] = useState<Record<string, string>>({});
  const d = q.data;

  const report = async (id: string) => {
    try {
      await api(`/commissions/${id}/report`, { body: { reference: refs[id]?.trim() } });
      toast('Gracias. Verificaremos la transferencia.', 'success');
      await qc.invalidateQueries({ queryKey: ['commissions'] });
    } catch (e) {
      toast(errorMessage(e), 'danger');
    }
  };

  return (
    <Page title="Comisión semanal" back="/driver/more" onRefresh={() => q.refetch()}>
      {q.isLoading && <Spinner />}
      {d && (
        <div className="flex flex-col gap-4">
          {d.blocked && <CommissionBlockedNotice amount={d.blocked.amount} />}
          <Card className="p-5">
            <p className="text-sm text-[var(--text-muted)]">Por pagar</p>
            <p className="text-3xl font-extrabold">{formatMoney(d.pending)}</p>
            <p className="text-xs text-[var(--text-muted)] mt-1">
              Cada lunes se calcula el {Number(d.percent)}% de lo que cobraste por viajes la semana anterior. Las propinas son 100% tuyas.
              {d.enabled && d.blocking && ` Tienes ${d.graceDays} día${d.graceDays === 1 ? '' : 's'} para pagarla; si vence, tu cuenta se pausa hasta que la pagues.`}
            </p>
          </Card>
          {Number(d.pending) > 0 && d.bankAccounts.length > 0 && (
            <Card className="p-4 flex flex-col gap-2 text-sm">
              <p className="font-semibold flex items-center gap-2"><Landmark className="size-5" aria-hidden /> Transfiere a</p>
              {d.bankAccounts.map((b, i) => (
                <p key={i}>
                  <strong>{b.bank}</strong> · {b.accountType} · <span className="font-mono">{b.number}</span> · {b.holder}
                  {b.holderId ? ` (${b.holderId})` : ''}
                </p>
              ))}
            </Card>
          )}
          {d.items.length === 0 && <EmptyState title="Sin comisiones" description={d.enabled ? 'Aquí verás la comisión de cada semana.' : 'La comisión semanal no está activa.'} />}
          {d.items.map((c) => (
            <Card key={c.id} className="p-4 flex flex-col gap-2">
              <div className="flex items-center justify-between gap-2">
                <p className="font-semibold">{week(c)}</p>
                <Badge tone={STATUS[c.status].tone}>{STATUS[c.status].label}</Badge>
              </div>
              <p className="text-sm">
                {Number(c.percent)}% de {formatMoney(c.earnings)} = <strong>{formatMoney(c.amount)}</strong>
              </p>
              {c.status === 'PENDING' && (
                <p className={cn('text-xs', new Date(c.dueAt) < new Date() ? 'text-brand-red font-semibold' : 'text-[var(--text-muted)]')}>
                  {new Date(c.dueAt) < new Date() ? 'Venció el' : 'Paga antes del'} {fmtDateTime(c.dueAt)}
                </p>
              )}
              {c.reference && <p className="text-xs text-[var(--text-muted)]">Referencia: {c.reference}</p>}
              {c.note && <p className="text-xs text-[var(--text-muted)]">{c.note}</p>}
              {c.status === 'PENDING' && (
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <TextField label="Referencia de la transferencia" value={refs[c.id] ?? ''} onChange={(e) => setRefs({ ...refs, [c.id]: e.target.value })} />
                  </div>
                  <Button size="sm" disabled={(refs[c.id]?.trim().length ?? 0) < 2} onClick={() => void report(c.id)}>Ya transferí</Button>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </Page>
  );
}

/** Aviso de cuenta pausada por comisión vencida (en Comisiones y en el panel del conductor). */
export function CommissionBlockedNotice({ amount, link }: { amount: string; link?: boolean }) {
  return (
    <Card className="p-4 flex gap-3 border-2 border-brand-red bg-brand-red/5">
      <Lock className="size-6 text-brand-red shrink-0" aria-hidden />
      <div className="text-sm">
        <p className="font-bold">Tu cuenta está pausada</p>
        <p className="text-[var(--text-muted)]">
          Tienes {formatMoney(amount)} de comisión vencida. No puedes conectarte, cotizar ni aceptar viajes nuevos hasta que transfieras y toques "Ya transferí". Tus viajes ya confirmados sí puedes hacerlos.
        </p>
        {link && <Link to="/driver/commissions" className="inline-block mt-1 font-semibold text-brand-red underline">Pagar la comisión</Link>}
      </div>
    </Card>
  );
}

/** Administración: comisiones de todos los conductores; marcar como pagada, perdonarla o rechazar el pago reportado. */
export function CommissionsAdminPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [status, setStatus] = useState<'REPORTED' | 'PENDING' | 'PAID'>('REPORTED');
  const q = useQuery({ queryKey: ['commissions', 'admin', status], queryFn: () => api<CommissionDto[]>('/commissions', { query: { status } }) });
  const [notes, setNotes] = useState<Record<string, string>>({});

  const settle = async (id: string, s: 'PAID' | 'WAIVED' | 'PENDING') => {
    try {
      await api(`/commissions/${id}`, { method: 'PATCH', body: { status: s, note: notes[id]?.trim() || undefined } });
      toast(s === 'PAID' ? 'Marcada como pagada' : s === 'WAIVED' ? 'Comisión perdonada' : 'Pago rechazado: vuelve a quedar pendiente', s === 'PENDING' ? 'dark' : 'success');
      await qc.invalidateQueries({ queryKey: ['commissions'] });
    } catch (e) {
      toast(errorMessage(e), 'danger');
    }
  };

  return (
    <Page title="Comisiones" back="/driver/more" onRefresh={() => q.refetch()}>
      <div className="flex flex-col gap-4">
        <Notice>Se activan y se ajusta el porcentaje en Configuración → Comisión semanal. Los administradores no pagan comisión. Si el bloqueo está activo, el conductor con comisión vencida queda pausado hasta que reporte el pago.</Notice>
        <div role="radiogroup" aria-label="Estado" className="grid grid-cols-3 gap-2">
          {([
            ['REPORTED', 'Por verificar'],
            ['PENDING', 'Pendientes'],
            ['PAID', 'Pagadas'],
          ] as const).map(([s, l]) => (
            <button key={s} role="radio" aria-checked={status === s} onClick={() => setStatus(s)} className={cn('tap h-11 rounded-2xl border text-sm font-semibold', status === s ? 'bg-brand-black text-white border-brand-black dark:bg-white dark:text-brand-black' : 'border-[var(--border)] bg-[var(--surface)]')}>
              {l}
            </button>
          ))}
        </div>
        {q.isLoading && <Spinner />}
        {q.data?.length === 0 && <EmptyState title="No hay comisiones aquí" />}
        {q.data?.map((c) => (
          <Card key={c.id} className="p-4 flex flex-col gap-2 text-sm">
            <div className="flex items-center justify-between gap-2">
              <p className="font-semibold">{c.driver}</p>
              <Badge tone={STATUS[c.status].tone}>{STATUS[c.status].label}</Badge>
            </div>
            <p>
              {week(c)} · {Number(c.percent)}% de {formatMoney(c.earnings)} = <strong>{formatMoney(c.amount)}</strong>
            </p>
            {c.reference && <p className="text-[var(--text-muted)]">Referencia: {c.reference}</p>}
            {c.status === 'PENDING' && <p className={new Date(c.dueAt) < new Date() ? 'text-brand-red font-semibold' : 'text-[var(--text-muted)]'}>{new Date(c.dueAt) < new Date() ? 'Vencida desde' : 'Vence'} {fmtDateTime(c.dueAt)}</p>}
            {(c.status === 'PENDING' || c.status === 'REPORTED') && (
              <>
                <TextArea label="Nota (opcional)" value={notes[c.id] ?? ''} onChange={(e) => setNotes({ ...notes, [c.id]: e.target.value })} maxLength={300} />
                <div className={cn('grid gap-2', c.status === 'REPORTED' ? 'grid-cols-3' : 'grid-cols-2')}>
                  {c.status === 'REPORTED' && <Button size="sm" variant="ghost" onClick={() => void settle(c.id, 'PENDING')}>No llegó</Button>}
                  <Button size="sm" variant="secondary" onClick={() => void settle(c.id, 'WAIVED')}>Perdonar</Button>
                  <Button size="sm" onClick={() => void settle(c.id, 'PAID')}>Marcar pagada</Button>
                </div>
              </>
            )}
          </Card>
        ))}
      </div>
    </Page>
  );
}
